import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const EXERCISES = [
  'Bench Press','Incline Bench Press','Decline Bench Press','Dumbbell Fly','Cable Fly',
  'Push Up','Chest Dip','Pec Deck','Incline Dumbbell Press','Close Grip Bench Press',
  'Pull Up','Chin Up','Barbell Row','Dumbbell Row','Cable Row','Lat Pulldown',
  'T-Bar Row','Deadlift','Romanian Deadlift','Face Pull','Shrug','Rack Pull',
  'Overhead Press','Dumbbell Shoulder Press','Arnold Press','Lateral Raise',
  'Front Raise','Rear Delt Fly','Upright Row','Cable Lateral Raise',
  'Barbell Curl','Dumbbell Curl','Hammer Curl','Preacher Curl','Cable Curl',
  'Tricep Pushdown','Skull Crusher','Overhead Tricep Extension','Dip','Diamond Push Up',
  'Concentration Curl','Cable Tricep Kickback',
  'Squat','Back Squat','Front Squat','Leg Press','Leg Curl',
  'Leg Extension','Calf Raise','Bulgarian Split Squat','Lunge','Step Up',
  'Hack Squat','Sumo Deadlift','Hip Thrust','Glute Bridge','Goblet Squat',
  'Plank','Crunch','Sit Up','Leg Raise','Russian Twist','Cable Crunch',
  'Ab Wheel Rollout','Hanging Leg Raise','Side Plank','Bicycle Crunch',
  'Burpee','Mountain Climber','Jump Rope','Box Jump','Kettlebell Swing',
  'Clean and Press','Snatch','Turkish Get Up','Battle Ropes','Farmer Walk',
  'Chest Press Machine','Row Machine','Shoulder Press Machine','Assisted Pull Up',
  'Smith Machine Squat','Cable Crossover',
]

const HEALTH_CAP = 60

function getLocalDate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

function applyCurve(raw) {
  if (raw <= 0) return 0
  if (raw >= 100) return 100
  return Math.min(100, Math.round(Math.pow(raw / 100, 0.65) * 100))
}

export default function FitnessPage({ onNavigate }) {
  const { user, profile } = useAuth()
  const userGoals = profile?.goals || []
  const storageKey = `workout_log_${getLocalDate()}`

  // Workout being built
  const [exercises, setExercises] = useState([{ name: '', sets: 3, reps: 10, weight: 0 }])
  const [unit, setUnit] = useState(() => localStorage.getItem('ls_weight_unit') || 'lbs')
  const [autocomplete, setAutocomplete] = useState({})

  const [workoutStarted, setWorkoutStarted] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [saved, setSaved] = useState(false)
  const [habitPrompt, setHabitPrompt] = useState(null) // array of habits to prompt

  async function confirmHabitComplete() {
    if (!habitPrompt) return
    for (const habit of habitPrompt) {
      await supabase.from('habit_completions').upsert({
        user_id: user.id,
        habit_id: habit.id,
        date: getLocalDate(),
      }, { onConflict: 'user_id,habit_id,date' })
    }
    setHabitPrompt(null)
  }
  const [healthPtsEarned, setHealthPtsEarned] = useState(0)

  // Past workouts today (from localStorage)
  const [todayWorkouts, setTodayWorkouts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '[]') } catch { return [] }
  })

  // Track health points used today
  const healthPtsUsed = todayWorkouts.reduce((sum, w) => sum + (w.healthPts || 0), 0)
  const healthPtsRemaining = Math.max(0, HEALTH_CAP - healthPtsUsed)

  function toggleUnit() {
    const next = unit === 'lbs' ? 'kg' : 'lbs'
    setUnit(next)
    localStorage.setItem('ls_weight_unit', next)
  }

  function addExercise() {
    setExercises(prev => [...prev, { name: '', sets: 3, reps: 10, weight: 0 }])
  }

  function removeExercise(i) {
    setExercises(prev => prev.filter((_, idx) => idx !== i))
  }

  function updateExercise(i, field, value) {
    setExercises(prev => prev.map((ex, idx) => idx === i ? { ...ex, [field]: value } : ex))
    if (field === 'name') {
      const q = value.toLowerCase()
      const matches = q.length > 0 ? EXERCISES.filter(e => e.toLowerCase().includes(q)).slice(0, 5) : []
      setAutocomplete(prev => ({ ...prev, [i]: matches }))
    }
  }

  function selectSuggestion(i, name) {
    setExercises(prev => prev.map((ex, idx) => idx === i ? { ...ex, name } : ex))
    setAutocomplete(prev => ({ ...prev, [i]: [] }))
  }

  async function saveWorkout() {
    const valid = exercises.filter(e => e.name.trim())
    if (valid.length === 0 || analyzing) return
    if (healthPtsRemaining <= 0) return

    setAnalyzing(true)
    setSaved(false)

    // Build workout summary for AI
    const workoutSummary = valid.map(e =>
      `${e.name}: ${e.sets} sets × ${e.reps} reps${e.weight > 0 ? ` @ ${e.weight}${unit}` : ''}`
    ).join(', ')

    let healthPts = 20 // fallback
    let purposePts = 0

    try {
      // Send full workout details to Claude with a workout-specific prompt
      const workoutPrompt = `You are scoring a workout for a fitness tracking app. Analyze this workout and give it a health score from 1-100 based on volume, intensity, and effort.

Workout logged:
${valid.map(e => `- ${e.name}: ${e.sets} sets × ${e.reps} reps${e.weight > 0 ? ` @ ${e.weight}${unit}` : ''}`).join('\n')}
Total exercises: ${valid.length}

Scoring guide:
- Light workout (1-2 exercises, low volume): 20-35
- Moderate workout (3-4 exercises, decent volume): 36-55
- Solid workout (5-6 exercises, good volume/weight): 56-72
- Strong workout (7+ exercises OR very high weight/volume): 73-85
- Elite session (high volume, heavy weights, many exercises): 86-100

Respond ONLY with a JSON object like this, no other text:
{"health_points": 65, "summary": "one sentence describing the workout"}`

      const res = await fetch('/api/coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: workoutPrompt })
      })
      const data = await res.json()
      if (data.text) {
        const clean = data.text.replace(/```json|```/g, '').trim()
        const parsed = JSON.parse(clean)
        healthPts = Math.min(Math.max(parsed.health_points || 20, 1), healthPtsRemaining)
      }
    } catch {
      healthPts = Math.min(20, healthPtsRemaining)
    }

    // Purpose points for athletic/fitness goals
    const fitnessGoals = ['athletic', 'fitness']
    if (userGoals.some(g => fitnessGoals.includes(g))) {
      purposePts = Math.round(healthPts * 0.5)
    }

    // Save health points to manual_tasks
    if (user && healthPts > 0) {
      await supabase.from('manual_tasks').insert({
        user_id: user.id,
        date: getLocalDate(),
        description: `Workout: ${workoutSummary}`,
        category: 'Workout',
        area: 'health',
        base_points: healthPts,
        time_minutes: 45,
        final_points: healthPts,
        flagged: false,
        flag_reason: '',
      })

      if (purposePts > 0) {
        await supabase.from('manual_tasks').insert({
          user_id: user.id,
          date: getLocalDate(),
          description: `Workout goal progress`,
          category: 'Workout Goal',
          area: 'purpose',
          base_points: purposePts,
          time_minutes: 45,
          final_points: purposePts,
          flagged: false,
          flag_reason: '',
        })
      }
    }

    // Check for workout habits and prompt user
    if (user) {
      const WORKOUT_KEYWORDS = ['workout','exercise','weightlift','weights','gym','lift','training','run','cardio','crossfit','jog','swim','cycling','bike','hiit','strength']
      const { data: userHabits } = await supabase
        .from('user_habits').select('*').eq('user_id', user.id)
      if (userHabits) {
        const todayIndex = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1
        const todayWorkoutHabits = userHabits.filter(h =>
          WORKOUT_KEYWORDS.some(k => h.title.toLowerCase().includes(k)) &&
          (h.days || [0,1,2,3,4,5,6]).includes(todayIndex)
        )
        if (todayWorkoutHabits.length > 0) {
          setHabitPrompt(todayWorkoutHabits)
        }
      }
    }

    // Save to localStorage for display (resets at midnight)
    const workoutEntry = {
      id: Date.now(),
      exercises: valid,
      unit,
      healthPts,
      purposePts,
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    }
    const updated = [workoutEntry, ...todayWorkouts]
    localStorage.setItem(storageKey, JSON.stringify(updated))
    setTodayWorkouts(updated)
    setHealthPtsEarned(healthPts)

    // Reset form
    setExercises([{ name: '', sets: 3, reps: 10, weight: 0 }])
    setWorkoutStarted(false)
    setAnalyzing(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 4000)
  }

  return (
    <>
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.backBtn} onClick={() => onNavigate('dashboard')}>←</div>
        <div style={s.headerTitle}>Workout Logger</div>
        <div style={{ width: 36 }} />
      </div>

      <div style={s.main}>
        <div style={s.subText}>
          Add your exercises below, then tap "Log Workout" — your whole session is analyzed at once.
          {healthPtsRemaining < HEALTH_CAP && (
            <span style={{ color: '#4caf7d' }}> {healthPtsRemaining} health pts remaining today.</span>
          )}
        </div>

        {saved && (
          <div style={s.savedBanner}>
            ✓ Workout saved! +{applyCurve(healthPtsEarned)} health pts added to your score.
          </div>
        )}

        {healthPtsRemaining <= 0 && (
          <div style={s.limitBanner}>
            You've maxed out your health points for today (60 pts). Come back tomorrow!
          </div>
        )}

        {/* EXERCISE LIST */}
        <div style={s.card}>
          {!workoutStarted ? (
            <div style={s.startBtn} onClick={() => setWorkoutStarted(true)}>
              Start Workout →
            </div>
          ) : (
            <>
          <div style={s.unitRow}>
            <div style={s.fieldLabel}>Weight Unit</div>
            <div style={s.unitToggle} onClick={toggleUnit}>
              <div style={{ ...s.unitOpt, background: unit === 'lbs' ? '#c9a84c' : 'transparent', color: unit === 'lbs' ? '#000' : '#6b6860' }}>lbs</div>
              <div style={{ ...s.unitOpt, background: unit === 'kg' ? '#c9a84c' : 'transparent', color: unit === 'kg' ? '#000' : '#6b6860' }}>kg</div>
            </div>
          </div>

          {exercises.map((ex, i) => (
            <div key={i} style={s.exBlock}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    style={s.exInput}
                    placeholder="Exercise name..."
                    value={ex.name}
                    onChange={e => updateExercise(i, 'name', e.target.value)}
                  />
                  {autocomplete[i]?.length > 0 && (
                    <div style={s.acList}>
                      {autocomplete[i].map(sug => (
                        <div key={sug} style={s.acItem} onClick={() => selectSuggestion(i, sug)}>{sug}</div>
                      ))}
                    </div>
                  )}
                </div>
                {exercises.length > 1 && (
                  <div style={s.removeBtn} onClick={() => removeExercise(i)}>✕</div>
                )}
              </div>
              <div style={s.exStats}>
                {['sets', 'reps', 'weight'].map(field => (
                  <div key={field}>
                    <div style={s.exStatLabel}>{field === 'weight' ? `${field} (${unit})` : field}</div>
                    <input
                      style={s.exStatInput}
                      type="number" min="0"
                      value={ex[field]}
                      onChange={e => updateExercise(i, field, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div style={s.addExBtn} onClick={addExercise}>+ Add Exercise</div>

          <div style={{
            ...s.logBtn,
            opacity: analyzing || exercises.every(e => !e.name.trim()) || healthPtsRemaining <= 0 ? 0.5 : 1
          }} onClick={saveWorkout}>
            {analyzing ? 'Analyzing your workout...' : 'End Workout →'}
          </div>
          </>
          )}
        </div>

        {/* TODAY'S LOGGED WORKOUTS */}
        {todayWorkouts.length > 0 && (
          <div style={s.section}>
            <div style={s.sectionTitle}>Today's Workouts</div>
            {todayWorkouts.map((workout, wi) => (
              <div key={workout.id} style={s.workoutCard}>
                <div style={s.workoutHeader}>
                  <div style={s.workoutTime}>{workout.time}</div>
                  <div style={s.workoutPts}>+{applyCurve(workout.healthPts)} health pts</div>
                </div>
                {workout.exercises.map((ex, ei) => (
                  <div key={ei} style={s.workoutExRow}>
                    <div style={s.workoutExName}>{ex.name}</div>
                    <div style={s.workoutExMeta}>
                      {ex.sets}×{ex.reps}{ex.weight > 0 ? ` @ ${ex.weight}${workout.unit}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

      {/* HABIT PROMPT BUBBLE */}
      {habitPrompt && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 400 }} onClick={() => setHabitPrompt(null)} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px', padding: '28px 24px', zIndex: 401, width: '300px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>💪</div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '2px', color: '#e8e6df', marginBottom: '8px' }}>Mark Habit Complete?</div>
            <div style={{ fontSize: '13px', color: '#6b6860', marginBottom: '24px', lineHeight: '1.5' }}>
              Want to mark "{habitPrompt.map(h => h.title).join(', ')}" as done for today?
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1, padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', color: '#6b6860', fontSize: '14px', cursor: 'pointer' }}
                onClick={() => setHabitPrompt(null)}>Skip</div>
              <div style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', borderRadius: '12px', color: '#000', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}
                onClick={confirmHabitComplete}>Yes! ✓</div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

const s = {
  page: { minHeight: '100vh', background: '#111118', color: '#e8e6df', fontFamily: "'DM Sans', sans-serif" },
  header: { position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'max(18px, env(safe-area-inset-top))', paddingBottom: '18px', paddingLeft: '24px', paddingRight: '24px', background: 'rgba(10,10,18,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.12)' },
  backBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.07)', borderRadius: '10px', cursor: 'pointer', fontSize: '18px', color: '#c9a84c' },
  headerTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '3px', color: '#4caf7d' },
  main: { maxWidth: '420px', margin: '0 auto', padding: '20px 20px 100px' },
  subText: { fontSize: '13px', color: '#6b6860', marginBottom: '16px', lineHeight: '1.6' },
  savedBanner: { background: 'rgba(76,175,125,0.12)', border: '1px solid rgba(76,175,125,0.25)', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#4caf7d', marginBottom: '16px' },
  limitBanner: { background: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#e05c5c', marginBottom: '16px' },
  card: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '20px', marginBottom: '24px' },
  unitRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
  fieldLabel: { fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860' },
  unitToggle: { display: 'flex', background: 'rgba(255,255,255,0.07)', borderRadius: '8px', overflow: 'hidden' },
  unitOpt: { padding: '7px 14px', fontSize: '12px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.15s' },
  exBlock: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '12px', marginBottom: '10px' },
  exInput: { width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.13)', borderRadius: '8px', padding: '10px 12px', color: '#e8e6df', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box' },
  acList: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#1a1a28', border: '1px solid rgba(255,255,255,0.13)', borderRadius: '8px', zIndex: 50, marginTop: '2px' },
  acItem: { padding: '10px 12px', fontSize: '13px', color: '#e8e6df', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  exStats: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' },
  exStatLabel: { fontSize: '10px', color: '#6b6860', marginBottom: '4px', letterSpacing: '1px', textTransform: 'uppercase' },
  exStatInput: { width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.13)', borderRadius: '6px', padding: '8px', color: '#e8e6df', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box', textAlign: 'center' },
  removeBtn: { fontSize: '14px', color: '#6b6860', cursor: 'pointer', padding: '4px 8px', flexShrink: 0 },
  addExBtn: { padding: '10px', background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '8px', color: '#6b6860', fontSize: '13px', textAlign: 'center', cursor: 'pointer', marginBottom: '14px' },
  startBtn: { padding: '16px', background: 'linear-gradient(135deg, #4caf7d, #2d7a45)', color: '#000', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', fontSize: '15px', fontWeight: '700' },
  logBtn: { padding: '14px', background: 'linear-gradient(135deg, #e05c5c, #a83030)', color: '#fff', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', fontSize: '14px', fontWeight: '700', transition: 'opacity 0.2s' },
  section: { marginBottom: '24px' },
  sectionTitle: { fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '12px' },
  workoutCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '16px', marginBottom: '10px' },
  workoutHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  workoutTime: { fontSize: '12px', color: '#6b6860' },
  workoutPts: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '16px', color: '#4caf7d', letterSpacing: '0.5px' },
  workoutExRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', marginBottom: '4px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  workoutExName: { fontSize: '13px', color: '#e8e6df', fontWeight: '500' },
  workoutExMeta: { fontSize: '12px', color: '#6b6860' },
}
